import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate, ESTATUS_TRAMITE, PRIORIDAD_STYLE } from "../lib/format";
import { Card, StatusBadge, ProgressBar } from "../components/shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Plus, Search } from "lucide-react";
import { LoteIA } from "../components/CapturaIA";

const CATEGORIAS = ["Tramitología de la tierra", "Permiso Municipal", "Permiso Estatal", "Permiso Federal"];

function NuevoTramiteDialog({ pid, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nombre: "", categoria: CATEGORIAS[0], autoridad: "", descripcion: "", prioridad: "Media", requisitos: "" });
  const submit = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    try {
      await api.post("/tramites", {
        property_id: pid, categoria: form.categoria, nombre: form.nombre, autoridad: form.autoridad,
        descripcion: form.descripcion, prioridad: form.prioridad,
        requisitos: form.requisitos.split("\n").map((r) => r.trim()).filter(Boolean),
      });
      toast.success("Trámite creado");
      setOpen(false);
      setForm({ nombre: "", categoria: CATEGORIAS[0], autoridad: "", descripcion: "", prioridad: "Media", requisitos: "" });
      onCreated();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="nuevo-tramite-button" className="bg-[#00A3B5] hover:bg-[#008C9C] text-white gap-2 active:scale-[0.98]">
          <Plus size={15} /> Trámite ad-hoc
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-white max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">Nuevo trámite ad-hoc</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input data-testid="nuevo-tramite-nombre" placeholder="Nombre del trámite" value={form.nombre}
            onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
              <SelectTrigger data-testid="nuevo-tramite-categoria"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.prioridad} onValueChange={(v) => setForm({ ...form, prioridad: v })}>
              <SelectTrigger data-testid="nuevo-tramite-prioridad"><SelectValue /></SelectTrigger>
              <SelectContent>{["Alta", "Media", "Baja"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Input data-testid="nuevo-tramite-autoridad" placeholder="Autoridad emisora" value={form.autoridad}
            onChange={(e) => setForm({ ...form, autoridad: e.target.value })} />
          <Textarea placeholder="Descripción" value={form.descripcion} rows={2}
            onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
          <Textarea placeholder="Requisitos (uno por línea)" value={form.requisitos} rows={3}
            onChange={(e) => setForm({ ...form, requisitos: e.target.value })} />
          <Button data-testid="nuevo-tramite-submit" onClick={submit} className="w-full bg-[#002B49] text-white">Crear trámite</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Tramites() {
  const { pid } = useParams();
  const { canEdit } = useAuth();
  const [tramites, setTramites] = useState(null);
  const [fEstatus, setFEstatus] = useState("");
  const [fCategoria, setFCategoria] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [openEtapas, setOpenEtapas] = useState({});

  const load = () => api.get(`/properties/${pid}/tramites`).then((r) => setTramites(r.data));
  useEffect(() => { setTramites(null); load(); }, [pid]); // eslint-disable-line

  const grupos = useMemo(() => {
    if (!tramites) return [];
    const filtered = tramites.filter((t) =>
      (!fEstatus || t.estatus === fEstatus) &&
      (!fCategoria || t.categoria === fCategoria) &&
      (!busqueda || t.nombre.toLowerCase().includes(busqueda.toLowerCase()) || (t.autoridad || "").toLowerCase().includes(busqueda.toLowerCase())));
    const map = {};
    filtered.forEach((t) => { (map[t.etapa] = map[t.etapa] || []).push(t); });
    return Object.entries(map);
  }, [tramites, fEstatus, fCategoria, busqueda]);

  const avgEtapa = (ts) => {
    const vals = ts.filter((t) => t.estatus !== "No aplica").map((t) => t.avance ?? 0);
    return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1200px]">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Fase 1 · Módulo funcional</p>
          <h1 className="font-display text-3xl font-bold text-[#002B49]">Tramitología y Permisología</h1>
        </div>
        {canEdit && <NuevoTramiteDialog pid={pid} onCreated={load} />}
      </motion.div>

      {canEdit && <div className="mb-6"><LoteIA pid={pid} onChanged={load} /></div>}

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="buscar-tramites" placeholder="Buscar trámite o autoridad..." value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)} className="pl-9 w-64 h-9 bg-white" />
        </div>
        <Select value={fEstatus || "todos"} onValueChange={(v) => setFEstatus(v === "todos" ? "" : v)}>
          <SelectTrigger data-testid="filtro-estatus-tramites" className="w-56 h-9 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los estatus</SelectItem>
            {ESTATUS_TRAMITE.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCategoria || "todas"} onValueChange={(v) => setFCategoria(v === "todas" ? "" : v)}>
          <SelectTrigger data-testid="filtro-categoria-tramites" className="w-56 h-9 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las categorías</SelectItem>
            {CATEGORIAS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {!tramites ? <p className="text-slate-400 animate-pulse">Cargando trámites...</p> : (
        <div className="space-y-4">
          {grupos.map(([etapa, ts]) => {
            const open = openEtapas[etapa] !== false;
            return (
              <Card key={etapa} testId={`etapa-${etapa}`}>
                <button onClick={() => setOpenEtapas({ ...openEtapas, [etapa]: !open })}
                  className="w-full flex items-center gap-3 p-5 text-left">
                  {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
                  <div className="flex-1">
                    <p className="font-display font-bold text-[#002B49]">{etapa}</p>
                    <p className="text-xs text-slate-500">{ts.length} trámites · {ts.filter((t) => t.estatus === "Aprobado").length} aprobados</p>
                  </div>
                  <div className="w-40">
                    <p className="text-right text-xs font-bold text-[#002B49] mb-1">{avgEtapa(ts)}%</p>
                    <ProgressBar value={avgEtapa(ts)} />
                  </div>
                </button>
                {open && (
                  <div className="border-t border-slate-100">
                    {ts.map((t) => (
                      <Link key={t.id} to={`/p/${pid}/tramites/${t.id}`} data-testid={`tramite-row-${t.id}`}
                        className="flex items-center gap-4 px-5 py-3.5 border-b border-slate-50 last:border-0 hover:bg-slate-50 transition-colors group">
                        <span className="text-xs font-bold text-slate-300 w-6 text-right">{t.orden || "·"}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 group-hover:text-[#002B49] truncate">{t.nombre}</p>
                          <p className="text-xs text-slate-400 truncate">
                            {t.autoridad || "—"}{t.gestor ? ` · ${t.gestor}` : ""}{t.folio ? ` · Folio ${t.folio}` : ""}
                          </p>
                        </div>
                        {t.fecha_compromiso && <span className="text-xs text-slate-500 whitespace-nowrap hidden md:block">Compromiso: {fmtDate(t.fecha_compromiso)}</span>}
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${PRIORIDAD_STYLE[t.prioridad]}`}>{t.prioridad}</span>
                        <div className="w-24 hidden sm:block"><ProgressBar value={t.avance ?? 0} /></div>
                        <StatusBadge estatus={t.estatus} />
                      </Link>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
          {grupos.length === 0 && <p className="text-slate-400 text-center py-10">Sin trámites que coincidan con los filtros.</p>}
        </div>
      )}
    </div>
  );
}
